import { Outlet, useParams, Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef } from "react";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { SubscribePopupProvider } from "@/components/SubscribePopupContext";
import Header from "@/components/Header";
import RouteFallback from "@/components/RouteFallback";
import { trackPageView } from "@/lib/analytics";
import { startLinkPrefetcher } from "@/lib/linkPrefetcher";

// Lazy-load below-the-fold / deferred components to keep them out of the initial chunk.
// Footer renders below the hero, popups appear earliest after 7s, cookie consent after 6s.
const Footer = lazy(() => import("@/components/Footer"));
const GlobalSubscribePopup = lazy(() => import("@/components/GlobalSubscribePopup"));
const ProfileCompletionPopup = lazy(() => import("@/components/ProfileCompletionPopup"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));

const VALID_LOCALES = ["cs", "en"];

const LocaleLayout = () => {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();
  const initialPathRef = useRef<string | null>(null);

  // SPA pageview tracking. The initial pageview is sent by gtag('config') in
  // index.html; here we fire one for every subsequent client-side navigation.
  // Consent Mode automatically drops the hit if the user hasn't accepted.
  useEffect(() => {
    const fullPath = location.pathname + location.search;
    if (initialPathRef.current === null) {
      initialPathRef.current = fullPath;
      return;
    }
    if (initialPathRef.current === fullPath) return;
    initialPathRef.current = fullPath;
    // Defer one tick so document.title (set by SEOHead) is up to date.
    const t = setTimeout(() => trackPageView(fullPath), 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  // Idle-deferred: prefetch route chunks for any internal link that scrolls
  // into view. Doesn't run until the browser is idle, so it never competes
  // with the first paint or LCP work.
  useEffect(() => {
    startLinkPrefetcher();
  }, []);

  // Root "/" renders Czech content directly (no redirect) so the auto-detection
  // script in index.html can send non-Czech/Slovak users to /en without a
  // React-only double-redirect. Invalid non-root locales still fall back to /cs.
  const isRoot = location.pathname === "/";
  if (!isRoot && (!lang || !VALID_LOCALES.includes(lang))) {
    return <Navigate to="/cs" replace />;
  }

  return (
    <LanguageProvider>
      <SubscribePopupProvider
        renderPopup={(open, onClose) => (
          open ? (
            <Suspense fallback={null}>
              <GlobalSubscribePopup open={open} onClose={onClose} />
            </Suspense>
          ) : null
        )}
      >
        <div className="min-h-screen bg-background">
          <Header />
          {/* Per-route Suspense — keeps Header in the DOM while the route chunk loads,
              so the LCP element (header logo) renders immediately without waiting for
              page content. Null fallback avoids a layout-shifting spinner. */}
          {/* Min-height placeholder keeps the footer pinned to the bottom while
              the next route chunk loads, preventing the footer from jumping up
              into the viewport and then back down on navigation. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
          <Suspense
            fallback={
              <div aria-hidden="true" style={{ minHeight: "560px" }} className="bg-card border-t border-border" />
            }
          >
            <Footer />
          </Suspense>
        </div>
        <Suspense fallback={null}>
          <ProfileCompletionPopup />
          <CookieConsent />
        </Suspense>
      </SubscribePopupProvider>
    </LanguageProvider>
  );
};

export default LocaleLayout;
